import {
    Article,
    BulkFreight,
    CargoFreight,
    HandlingUnit,
    Packable,
    Shipment,
} from "./transportOrder"

export interface DangerousGoodsInformation {
    undg_code?: string | null
    description?: string | null
    exception_regulation?: string
    hazard_class?: HazardClass
    packing_group?: PackingGroup
    tunnel_restriction_code?: TunnelRestrictionCode
}

export enum HazardClass {
    CLASS_1_1 = "1.1",
    CLASS_1_2 = "1.2",
    CLASS_1_3 = "1.3",
    CLASS_1_4 = "1.4",
    CLASS_1_5 = "1.5",
    CLASS_1_6 = "1.6",
    CLASS_2_1 = "2.1",
    CLASS_2_2 = "2.2",
    CLASS_2_3 = "2.3",
    CLASS_3 = "3",
    CLASS_4_1 = "4.1",
    CLASS_4_2 = "4.2",
    CLASS_4_3 = "4.3",
    CLASS_5_1 = "5.1",
    CLASS_5_2 = "5.2",
    CLASS_6_1 = "6.1",
    CLASS_6_2 = "6.2",
    CLASS_7 = "7",
    CLASS_8 = "8",
    CLASS_9 = "9",
}

export enum PackingGroup {
    I = "I",
    II = "II",
    III = "III",
}

export enum TunnelRestrictionCode {
    NONE = "-",
    B = "B",
    B1000C = "B1000C",
    B_D = "B/D",
    B_E = "B/E",
    C = "C",
    C5000D = "C5000D",
    C_D = "C/D",
    C_E = "C/E",
    D = "D",
    D_E = "D/E",
    E = "E",
}

export function hasDangerousGoods(shipments: Shipment[]): boolean {
    return shipments.some(shipmentHasDangerousGoods)
}

function shipmentHasDangerousGoods(shipment: Shipment): boolean {
    switch (shipment.freight.type) {
    case "BULK_DRY":
    case "BULK_LIQUID":
        return bulkFreightHasDangerousGoods(shipment.freight as BulkFreight)
    case "CARGO_EMPTIES":
    case "CARGO_MATERIAL":
        return cargoFreightHasDangerousGoods(shipment.freight as CargoFreight)
    }
}

function bulkFreightHasDangerousGoods(freight: BulkFreight): boolean {
    return isDangerous(freight.dangerous_goods_information)
}

function cargoFreightHasDangerousGoods(freight: CargoFreight): boolean {
    return freight.handling_units.some(handlingUnitContainsDangerousGoods)
}

function handlingUnitContainsDangerousGoods(handlingUnit: HandlingUnit): boolean {
    return handlingUnit.content.some(packableContainsDangerousGoods)
}

function packableContainsDangerousGoods(packable: Packable): boolean {
    switch (packable.type) {
    case "ARTICLE":
        return articleIsDangerousGood(packable)
    case "HANDLING_UNIT": {
        return packable.content.some(packableContainsDangerousGoods)
    }}
}

export function articleIsDangerousGood(article: Article): boolean {
    return isDangerous(article.dangerous_goods_information)
}

function hasNonBlankUndgCode(dangerousGoodsInformation: DangerousGoodsInformation): boolean {
    return !!dangerousGoodsInformation.undg_code?.trim()?.length
}

// Concerning classes see https://en.wikipedia.org/wiki/Dangerous_goods#Classification_and_labeling_summary_tables
// For undg code see https://en.wikipedia.org/wiki/Lists_of_UN_numbers
export function isDangerous(dangerousGoodsInformation: DangerousGoodsInformation | undefined): boolean {
    if (dangerousGoodsInformation === undefined) {
        return false
    }
    return hasNonBlankUndgCode(dangerousGoodsInformation)
}
